import { useState, useEffect, useRef } from "react";

export const useTableFilters = <T extends Record<string, any>>(
  data: T[],
  initialFilters: { [key: string]: string[] } = {}
) => {
  const [filteredData, setFilteredData] = useState<T[]>(data);
  const [filterPanels, setFilterPanels] = useState<{ [key: string]: boolean }>({});
  const [filters, setFilters] = useState<{ [key: string]: string[] }>(initialFilters);
  const [searchQueries, setSearchQueries] = useState<{ [key: string]: string }>({});
  const panelRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  useEffect(() => {
    applyFilters();
  }, [filters, data]);

  const applyFilters = () => {
    const newFilteredData = data.filter((record) => {
      return Object.keys(filters).every((column) => {
        const selectedFilters = filters[column];
        if (!selectedFilters || selectedFilters.length === 0) {
          return true;
        }

        const recordValue = record[column];
        const isRecordEmpty = recordValue == null || recordValue === "";
        const emptyFilterSelected = selectedFilters.includes("");
        
        // Handle custom value checks if needed, currently string conversion
        const nonEmptyMatch =
          !isRecordEmpty && selectedFilters.includes(recordValue.toString());
        const emptyMatch = isRecordEmpty && emptyFilterSelected;

        return nonEmptyMatch || emptyMatch;
      });
    });
    setFilteredData(newFilteredData);
  };

  const toggleFilterPanel = (column: string) => {
    setFilterPanels((prevState) => {
      const newState = Object.keys(prevState).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as { [key: string]: boolean });
      return {
        ...newState,
        [column]: !prevState[column],
      };
    });
    setSearchQueries((prevState) => ({
      ...prevState,
      [column]: "",
    }));
  };

  const handleFilterChange = (column: string, value: string) => {
    setFilters((prevState) => {
      const columnFilters = prevState[column] || [];
      if (columnFilters.includes(value)) {
        return {
          ...prevState,
          [column]: columnFilters.filter((v) => v !== value),
        };
      } else {
        return {
          ...prevState,
          [column]: [...columnFilters, value],
        };
      }
    });
  };

  const handleFilterSearch = (column: string, query: string) => {
    setSearchQueries((prevState) => ({
      ...prevState,
      [column]: query,
    }));
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      Object.keys(panelRefs.current).some((key) => {
        const panel = panelRefs.current[key];
        return panel && !panel.contains(event.target as Node);
      })
    ) {
      setFilterPanels({});
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return {
    filteredData,
    filterPanels,
    filters,
    searchQueries,
    panelRefs,
    toggleFilterPanel,
    handleFilterChange,
    handleFilterSearch,
    setFilters, // Exported in case manual override is needed
  };
};
